import { useContext, useMemo } from 'react';

import { groupBy, orderBy } from 'lodash';

import { checkPermission } from '../../../utils/permission';
import { UserRoles } from '../../roster/types';
import { EditableListProps } from '../components/EditableList';
import { WhiteboardContext } from '../context/WhiteboardContext';
import { WhiteboardSessionContext } from '../context/WhiteboardSessionContext';
import { Page, PageTypes, SessionTypes } from '../types';
import { stringCompare } from '../util/misc';

export type WhiteboardPagesT = {
    primaryPages: EditableListProps<Page>['items'];
    secondaryPages: EditableListProps<Page>['secondaryItems'];
    pdfFiles: EditableListProps<Page>['pdfFiles'];
    templatePages: EditableListProps<Page>['templatePages'];
};

// Pages to display in the drawer.
export const useWhiteboardPages = (): WhiteboardPagesT => {
    const { userInfo } = useContext(WhiteboardContext);

    const { whiteboardSessionState } = useContext(WhiteboardSessionContext);
    const { pages, session } = whiteboardSessionState;

    return useMemo(() => {
        const isInstructor = checkPermission(UserRoles.Assistant, userInfo?.role ?? UserRoles.Student);

        const sortedSharedPages = pages.filter((p) => p.type === PageTypes.Shared);

        const sortedIndividualPages = pages
            .filter((p) => p.type === PageTypes.Individual)
            .filter((p) => isInstructor || p.created_by === userInfo?.student_id)
            .filter((p) => p.type !== PageTypes.Template)
            .sort((a, b) => stringCompare(a, b, 'name'));

        const pdfPages = pages
            .filter((p) => p.type === PageTypes.Pdf)
            .filter((p) => p.type !== PageTypes.Template)
            .filter((p) => p.order >= 0);
        const sortedPdfPages = orderBy(pdfPages, ['order', 'name'], ['asc', 'asc']);
        const groupedPdfPages = groupBy(sortedPdfPages, 'name');

        const templatePages = pages.filter((p) => p.type === PageTypes.Template);

        const pdfFiles: EditableListProps<Page>['pdfFiles'] = [];
        Object.entries(groupedPdfPages).forEach((file) => {
            pdfFiles.push({
                fileName: file[0],
                totalPages: file[1].length,
                pages: file[1]
            });
        });

        return {
            templatePages: session?.type === SessionTypes.Individual ? templatePages : [],
            primaryPages:
                session?.type === SessionTypes.Shared || session?.type === SessionTypes.Presentation
                    ? sortedSharedPages
                    : sortedIndividualPages,
            secondaryPages: session?.type === SessionTypes.Individual ? sortedSharedPages : sortedIndividualPages,
            pdfFiles
        };
    }, [userInfo, pages, session?.type]);
};

export const useSortedWhiteboardPages = (pages: WhiteboardPagesT) =>
    useMemo(() => {
        const { primaryPages, secondaryPages, pdfFiles, templatePages } = pages;

        const pdfPages = pdfFiles?.reduce((acc, file) => [...acc, ...file.pages], [] as Page[]);

        // prettier-ignore
        return [
            ...(templatePages ?? []),
            ...primaryPages,
            ...(secondaryPages ?? []),
            ...(pdfPages ?? [])
        ];
    }, [pages]);
